import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Crown, Laptop, Sparkles, BadgeCheck, Trophy, Compass, Handshake,
  Target, Flag, ArrowRight, GitCompareArrows,
} from 'lucide-react';
import { Button } from '../ui/button';

/**
 * HomeTrainingChooser — high-funnel "Find the Right Training for You"
 * section on the homepage. Contents (heading, subtitle, 4 cards, order,
 * enabled flag, position) are admin-editable via
 * `PUT /api/admin/homepage-training-chooser` and read at page-render
 * time from `GET /api/public/training-chooser`.
 *
 * Design guardrails (per user spec, Feb 26 2026):
 *  • 4-column desktop / 2-column tablet / 1-column mobile
 *  • Cards are equal height with bottom-aligned CTA buttons
 *  • Reuses accent palette from `CompareTrainingOptions.jsx` so this
 *    section reads as a sibling of the full comparison page
 *  • Below the grid: a single prominent "Compare All Training Options"
 *    button that deep-links to /compare-training-options
 *  • No large images — only lucide icons — so the homepage stays fast
 *
 * Rendering is driven by `settings` (fetched by PublicLanding). The
 * component is only mounted when `settings.enabled === true` OR the
 * caller passes `?preview=training-chooser` in the URL.
 */

const ICON_MAP = {
  users: Users,
  crown: Crown,
  laptop: Laptop,
  sparkles: Sparkles,
  badge_check: BadgeCheck,
  trophy: Trophy,
  compass: Compass,
  handshake: Handshake,
  target: Target,
  flag: Flag,
};

const iconFor = (key) => ICON_MAP[key] || ICON_MAP.users;

const HomeTrainingChooser = ({ settings }) => {
  const cards = useMemo(() => {
    const list = Array.isArray(settings?.cards) ? [...settings.cards] : [];
    return list.sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [settings]);

  if (!cards.length) return null;

  return (
    <section
      id="find-right-training"
      className="max-w-7xl mx-auto px-6 py-14 md:py-20"
      data-testid="home-training-chooser"
      aria-labelledby="training-chooser-heading"
    >
      <div className="text-center mb-10 md:mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F5F3FF] border border-[#E9D5FF] text-[10px] font-black uppercase tracking-widest text-[#7C3AED] mb-3">
          <Compass size={12} strokeWidth={2.5} />
          <span>Choose Your Path</span>
        </div>
        <h2
          id="training-chooser-heading"
          className="text-3xl md:text-5xl font-black tracking-tight text-[#0F172A]"
          data-testid="training-chooser-heading"
        >
          {settings?.heading || 'Find the Right Training for You'}
        </h2>
        <p
          className="text-muted-foreground mt-3 max-w-3xl mx-auto text-sm md:text-base leading-relaxed"
          data-testid="training-chooser-subtitle"
        >
          {settings?.subtitle}
        </p>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4"
        data-testid="training-chooser-grid"
      >
        {cards.map((c) => {
          const Ico = iconFor(c.icon_key);
          const accent = c.accent_hex || '#A855F7';
          return (
            <div
              key={c.key}
              className="group relative overflow-hidden rounded-2xl bg-white border-2 border-[#E5E7EB] hover:border-transparent hover:-translate-y-1 hover:shadow-2xl transition-all p-6 flex flex-col h-full min-h-[320px]"
              style={{ boxShadow: '0 1px 2px 0 rgba(0,0,0,0.04)' }}
              data-testid={`chooser-card-${c.key}`}
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                style={{ background: `linear-gradient(180deg, ${accent}14 0%, ${accent}04 60%, transparent 100%)` }}
                aria-hidden
              />

              <div className="relative flex-1 flex flex-col">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 shadow-sm"
                  style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}CC 100%)` }}
                >
                  <Ico size={22} className="text-white" strokeWidth={2.25} />
                </div>
                <h3
                  className="text-lg md:text-xl font-black tracking-tight leading-tight mb-2 text-[#0F172A]"
                  data-testid={`chooser-card-${c.key}-title`}
                >
                  {c.title}
                </h3>
                <p className="text-sm text-slate-600 leading-snug mb-3">
                  {c.description}
                </p>
                <div className="mt-1 rounded-lg bg-[#FAF9FF] border border-[#F1EBFF] px-3 py-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-[#7C3AED]/80 mb-0.5">
                    Best for
                  </div>
                  <div className="text-xs text-slate-700 leading-snug">
                    {c.best_for}
                  </div>
                </div>
              </div>

              <div className="relative mt-4">
                <Link to={c.cta_route} data-testid={`chooser-card-${c.key}-cta`}>
                  <Button
                    className="w-full h-11 text-white font-black uppercase tracking-widest text-[11px] hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: accent }}
                  >
                    {c.cta_label} <ArrowRight size={13} className="ml-1.5" />
                  </Button>
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* Compare-all deep link */}
      <div className="mt-8 md:mt-10 flex flex-col items-center gap-2 text-center">
        <div className="text-sm text-slate-500">
          Want the full side-by-side breakdown?
        </div>
        <Link to="/compare-training-options" data-testid="training-chooser-compare-all" className="w-full sm:w-auto max-w-full">
          <Button
            variant="outline"
            className="w-full sm:w-auto max-w-full whitespace-normal h-auto min-h-12 py-2.5 px-4 sm:px-6 rounded-xl border-2 border-[#7C3AED] text-[#5B21B6] hover:bg-[#F5F3FF] hover:border-[#5B21B6] font-black uppercase tracking-widest text-xs"
          >
            <GitCompareArrows size={14} className="mr-2 shrink-0" />
            Compare All Training Options
            <ArrowRight size={13} className="ml-2 shrink-0" />
          </Button>
        </Link>
      </div>
    </section>
  );
};

export default HomeTrainingChooser;
